import React, { useEffect, useState } from 'react';
import { NextPageWithLayout } from '@/pages/_app';
import { GetServerSidePropsContext, InferGetStaticPropsType } from 'next';
import { Input, Pagination, Select } from 'antd';
import Layouts from '@/layouts';
import {
  AlphabetSelector,
  Hero,
  FeaturedCreator,
  TrendingCreatorCard,
  StoreDirectoryCard,
} from '@/components/Designers';
import classNames from 'classnames';
import { useConfigProvider } from '@/lib/context';
import useBusinessSettingsDisplay from '@/lib/hooks/common/useBusinessSettingsDisplay';
import { getAllStores, getStoreListByHandles, logCommonView } from '@/lib/service';
import { useAsyncFn } from 'react-use';
import { Spinner } from '@/components/Common';
import { SEO_META } from '@/shared/seo/seo';
import { MetaInfo, SEO_META_PAGES } from '@/shared/types/seo';
import { useRouter } from 'next/router';
import Head from 'next/head';
import queryString from 'query-string';
import { EventSource } from '@/shared/types/event-types';
import './index.less';
export async function getServerSideProps(context: GetServerSidePropsContext) {
  return {
    props: {},
  };
}

const DesignersPage: NextPageWithLayout<
  InferGetStaticPropsType<typeof getServerSideProps>
> = () => {
  const { isMobile } = useConfigProvider();
  const router = useRouter();
  const { query } = router;
  const [selectValue, setSelectValue] = useState<number>(0);
  const [pageTotal, setPageTotal] = useState<number>(0);
  const [showNoSearchResultsMessage, setShowNoSearchResultsMessage] = useState<boolean>(false);
  const [trendingCreators, setTrendingCreators] = useState<any[]>([]);
  const [designers, setDesigners] = useState<any[]>([]);
  const { value: trendingCreatorsResults } = useBusinessSettingsDisplay<{
    value: { storeHandle: string }[];
  }>('CREATOR_MARKETPLACE_TRENDING_CREATORS');
  const [allStoresState, fetchAllStores] = useAsyncFn(getAllStores, []);

  useEffect(() => {
    if (trendingCreatorsResults && trendingCreatorsResults.value) {
      const storeHandles = (trendingCreatorsResults?.value || []).map((c) => c.storeHandle);
      getStoreListByHandles(storeHandles, {
        includeMediaUrls: false,
      }).then((response) => {
        if (response && response.list) {
          const creators = response.list;
          const _trendingCreators = creators.map((x: any) => {
            return { ...x, buttonText: 'Click to Shop' };
          });
          setTrendingCreators(_trendingCreators || []);
        }
      });
    }
  }, [trendingCreatorsResults]);

  useEffect(() => {
    fetchAllStores({
      ...query,
      sorts: query.sorts ? query.sorts : '-globalSort',
      size: isMobile ? 40 : 78,
      page: (query.page ? Number(query.page) : 1) - 1,
      prefixName: selectValue
        ? String.fromCharCode(0x60 + selectValue).toLocaleUpperCase()
        : undefined,
    }).then((response) => {
      const { list, total } = response;
      setPageTotal(total ?? 0);
      if (list && list.length > 0) {
        setShowNoSearchResultsMessage(false);
        const designers = (list || []).reduce(function (filtered: any[], option: { id: number }) {
          // Hacky way to hide some designers from showing up in browse designers flow for now.
          if (option.id !== 2349 && option.id !== 2368) {
            filtered.push(option);
          }
          return filtered;
        }, []);
        setDesigners(designers);
      } else {
        setShowNoSearchResultsMessage(true);
      }
    });
  }, [fetchAllStores, isMobile, query, selectValue]);

  const scrollToSearchResults = () => {
    const node = document.querySelector('.search-designers');
    if (node) {
      node.scrollIntoView(true);
    }
  };

  return (
    <div className="view-designers-page-outer">
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              SEO_META[SEO_META_PAGES.MARKETPLACE_DESIGNERS].jsonld({
                stores: designers || [],
              }),
            ),
          }}
        />
      </Head>
      <Hero />
      <AlphabetSelector
        offsetTop={isMobile ? 68 : 85}
        value={selectValue}
        onChange={(value) => {
          setDesigners([]);
          setShowNoSearchResultsMessage(false);
          setSelectValue(value);
          scrollToSearchResults();
        }}
      />
      <div
        className={classNames('view-designers-page', {
          'adjust-margin': isMobile,
        })}
      >
        <div className="trending-creators-container">
          <div className="title">Trending Creators</div>
          {isMobile ? (
            <FeaturedCreator creators={trendingCreators} />
          ) : (
            <div className="list-content">
              {(trendingCreators || []).map((creator, index) => (
                <TrendingCreatorCard key={index} creator={creator} />
              ))}
            </div>
          )}
        </div>
        <div className="view-designers-container list">
          <div className="section-header">
            <div className="title">Browse All Creators</div>
            <div className="search-designers">
              <div className="filter-dropdowns">
                <div className="select-item">
                  <Select
                    allowClear
                    placeholder="Sort"
                    size={'large'}
                    onSelect={(value) => {
                      router.replace(
                        `/s?${queryString.stringify({ ...query, page: 1, sorts: value })}`,
                      );
                    }}
                    onClear={() => {
                      router.replace(`/s?${queryString.stringify({ ...query, page: 1 })}`);
                    }}
                    options={[
                      {
                        label: 'Alphabetical',
                        value: '+title',
                      },
                      {
                        label: 'Newly Added',
                        value: '+createdAt',
                      },
                      {
                        label: 'Popularity',
                        value: '+globalSort',
                      },
                    ]}
                  ></Select>
                </div>
                <div className="select-item">
                  <div className="v-input">
                    <Input
                      size={'large'}
                      placeholder={'Instagram'}
                      onPressEnter={(e: any) => {
                        router
                          .replace(
                            `/s?${queryString.stringify({
                              ...query,
                              page: 1,
                              instagram: e.target?.value || undefined,
                            })}`,
                          )
                          .finally(() => {
                            logCommonView(
                              EventSource.CREATOR_INSTAGRAM_SEARCH,
                              e.target?.value || undefined,
                            );
                          });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {allStoresState.loading && <Spinner />}
          {showNoSearchResultsMessage && (
            <span className="no-results">We couldn&apos;t find any results for your search.</span>
          )}
          <div className="grid">
            {designers.map((designer, index) => (
              <StoreDirectoryCard store={designer} key={`designer-item-${index}`} />
            ))}
          </div>
          {designers.length > 0 && (
            <div className="pagination">
              <Pagination
                total={pageTotal}
                current={query.page ? Number(query.page) : 1}
                pageSize={isMobile ? 40 : 78}
                showSizeChanger={false}
                onChange={(page, pageSize) => {
                  router
                    .replace(`/s?${queryString.stringify({ ...query, page: page })}`)
                    .finally(() => {
                      setDesigners([]);
                      scrollToSearchResults();
                    });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DesignersPage.getLayout = (page) => {
  const head = SEO_META[SEO_META_PAGES.MARKETPLACE_DESIGNERS].head({
    title: 'Discover the Creator Community | Pietra',
    description:
      'Discover creator-led brands and support their small businesses in the largest Creator marketplace.',
    url: typeof window === 'object' ? window.location.href : '',
  });
  return (
    <Layouts
      head={head as MetaInfo}
      headers={SEO_META[SEO_META_PAGES.MARKETPLACE_DESIGNERS].headers}
    >
      {page}
    </Layouts>
  );
};

export default DesignersPage;
